import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { track } from "@/lib/analytics";
import { CREDITS, CERTS, SPOTIFY_PLAYLIST_ID, SPOTIFY_PLAYLIST_URL, IMAGES } from "@/lib/data";

const TONE = {
  gold: "bg-gradient-to-br from-yellow-100/80 via-amber-300/70 to-yellow-700/60 shadow-[0_0_30px_-6px_rgba(251,191,36,0.5)]",
  silver: "bg-gradient-to-br from-zinc-100/90 via-zinc-300/70 to-zinc-500/60 shadow-[0_0_30px_-6px_rgba(228,228,231,0.45)]",
  platinum: "bg-gradient-to-br from-white/90 via-zinc-200/70 to-zinc-400/50 shadow-[0_0_30px_-6px_rgba(255,255,255,0.4)]",
};

export const Work = () => {
  return (
    <section id="work" className="relative py-28 lg:py-36 bg-[#050505]" data-testid="work-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16">
          <div>
            <p className="text-[12px] tracking-[0.3em] uppercase text-white/40 mb-5">Selected Work</p>
            <h2 className="font-heading uppercase tracking-tight text-white text-4xl lg:text-6xl leading-[0.95]">
              Credits &amp; <span className="font-serif-accent italic lowercase normal-case tracking-normal text-gold">collaborations</span>
            </h2>
          </div>
          <p className="max-w-sm text-white/50 text-[15px] leading-relaxed">
            Six years of professional production — selected placements and collaborations, not a full list.
          </p>
        </div>

        <div className="border-t border-white/10">
          {CREDITS.map((c, i) => (
            <motion.a
              key={c.artist}
              href={c.link || undefined}
              target={c.link ? "_blank" : undefined}
              rel={c.link ? "noopener noreferrer" : undefined}
              onClick={() => c.link && track("credit_click", c.artist)}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.6, delay: i * 0.06, ease: [0.16, 1, 0.3, 1] }}
              className={`group border-b border-white/10 py-7 lg:py-9 flex items-center justify-between gap-6 transition-[padding] duration-500 ${c.link ? "hover:px-4 cursor-pointer" : ""}`}
              data-testid={`credit-${i}`}
            >
              <div className="flex items-baseline gap-6 lg:gap-10 min-w-0">
                <span className="font-serif-accent italic text-gold text-lg lg:text-xl w-8">{String(i + 1).padStart(2, "0")}</span>
                <h3 className="font-heading uppercase tracking-tight text-white text-3xl lg:text-5xl leading-none truncate group-hover:text-gold transition-colors">
                  {c.artist}
                </h3>
              </div>
              <div className="flex items-center gap-5 lg:gap-8 shrink-0">
                <span className="hidden sm:block text-[13px] text-white/45 group-hover:text-white/70 transition-colors">{c.note}</span>
                <span className="text-[10px] tracking-[0.2em] uppercase px-3 py-1 rounded-full border border-white/15 text-white/60">{c.tag}</span>
                <ArrowUpRight size={22} className="text-white/25 group-hover:text-gold group-hover:rotate-45 transition-[transform,color] duration-300" />
              </div>
            </motion.a>
          ))}
        </div>

        {/* Plaques & credibility — real photos */}
        <div className="mt-16 grid grid-cols-2 lg:grid-cols-3 gap-4 auto-rows-[220px] lg:auto-rows-[260px]">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-50px" }}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
            className="group relative overflow-hidden rounded-xl border border-white/10 col-span-2 lg:col-span-2 row-span-2"
            data-testid="plaque-img-wall"
          >
            <img src={IMAGES.plaqueWall} alt="Taps in front of the plaque wall" className="w-full h-full object-cover scale-105 group-hover:scale-100 transition-transform duration-700" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#050505]/70 via-transparent to-transparent" />
            <div className="absolute bottom-0 left-0 p-6">
              <p className="text-[10px] tracking-[0.2em] uppercase text-white/60">Credibility</p>
              <p className="font-heading uppercase tracking-tight text-white text-2xl leading-none mt-1">The wall</p>
            </div>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-50px" }}
            transition={{ duration: 0.7, delay: 0.08, ease: [0.16, 1, 0.3, 1] }}
            className="group relative overflow-hidden rounded-xl border border-white/10"
            data-testid="plaque-img-hold"
          >
            <img src={IMAGES.plaquesHold} alt="Taps holding platinum plaques" className="w-full h-full object-cover scale-105 group-hover:scale-100 transition-transform duration-700" />
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-50px" }}
            transition={{ duration: 0.7, delay: 0.14, ease: [0.16, 1, 0.3, 1] }}
            className="group relative overflow-hidden rounded-xl border border-white/10"
            data-testid="plaque-img-flat"
          >
            <img src={IMAGES.plaquesFlat} alt="Certified plaques and awards" className="w-full h-full object-cover scale-105 group-hover:scale-100 transition-transform duration-700" />
          </motion.div>
        </div>

        <div className="mt-14 grid grid-cols-2 lg:grid-cols-4 gap-4">
          {CERTS.map((c, i) => (
            <motion.div
              key={c.cert}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: i * 0.08 }}
              className="rounded-2xl border border-white/10 bg-gradient-to-b from-white/[0.05] to-transparent p-7 flex items-center gap-5"
              data-testid={`cert-${i}`}
            >
              <div className={`w-11 h-11 rounded-full shrink-0 ${TONE[c.tone] || TONE.gold}`} />
              <div>
                <div className="font-heading uppercase tracking-tight text-white text-2xl leading-none">{c.cert}</div>
                <div className="text-white/45 text-[13px] mt-1">{c.detail}</div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Spotify — produced by Taps */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="mt-16 rounded-2xl border border-white/10 bg-white/[0.02] p-6 lg:p-8"
          data-testid="spotify-block"
        >
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6">
            <div>
              <p className="text-[11px] tracking-[0.2em] uppercase text-white/40 mb-2">Listen</p>
              <h3 className="font-heading uppercase tracking-tight text-white text-2xl lg:text-3xl leading-none">
                Produced by <span className="font-serif-accent italic lowercase normal-case tracking-normal text-gold">TAPSS</span>
              </h3>
            </div>
            <a
              href={SPOTIFY_PLAYLIST_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => track("spotify_click", "work")}
              data-testid="spotify-open"
              className="group inline-flex items-center gap-2 px-6 py-3 rounded-full border border-white/20 text-white text-[12px] tracking-[0.12em] uppercase hover:bg-white/10 transition-colors duration-300 w-fit"
            >
              Open in Spotify <ArrowUpRight size={15} className="group-hover:rotate-45 transition-transform duration-300" />
            </a>
          </div>
          <iframe
            title="Produced by TAPSS — Spotify playlist"
            data-testid="spotify-iframe"
            src={`https://open.spotify.com/embed/playlist/${SPOTIFY_PLAYLIST_ID}?utm_source=generator&theme=0`}
            width="100%"
            height="420"
            frameBorder="0"
            loading="lazy"
            allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
            className="rounded-xl"
          />
        </motion.div>
      </div>
    </section>
  );
};

export default Work;
